import { useFileDownloadMeta } from "#src/hooks/useFileDownloadMeta";
import { ExceptionUtils } from "#src/utils/exception";
import { isFileScanComplete } from "#src/utils/fileDownload";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  colours,
  DataDisplayWithAdornment,
  FilePreviewPopover,
  Icon,
  useToast,
  type DataDisplayProps,
} from "@validereinc/common-components";
import { FileScanStatus, type FileDownloadMetaType } from "@validereinc/domain";
import {
  downloadFile,
  getBrowserRenderableFileType,
  getFileExtensionFromFilename,
  getMimeTypeFromExtension,
  getMimeTypeFromFileName,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import { validate } from "uuid";
import styles from "./FileDataDisplay.module.scss";

const cx = classNames.bind(styles);

export type FileDataDisplayProps = Omit<DataDisplayProps, "value"> & {
  fileName?: string;
  fileId?: string;
  /** A custom element to display in place of the default icon. */
  slotLeft?: ReactNode;
  /**
   * When true, fetches file metadata and contents on mount. When false
   * (default), they are fetched only after the preview popover opens.
   */
  prefetch?: boolean;
  /** hide the icon prefixed in the download link ?*/
  hideIcon?: boolean;
  /** Whether the filename should be truncated */
  shouldTruncateFilename?: boolean;
  /**
   * When true, the filename uses the surrounding font size, weight, and
   * line-height instead of the default button typography.
   */
  shouldInheritTypography?: boolean;
  /** Text shown when a file has been deemed unsafe by the file scanning service. */
  unsafeText?: string;
  /** Polling configuration while waiting for file scan status to resolve. */
  scanPollConfig?: {
    /** Milliseconds between scan-status polls while a scan is pending. Defaults to 1000ms. */
    pollSleepTimeMs?: number;
  };
};

/**
 * Displays a clickable filename that opens a preview popover.
 *
 * Metadata and file contents are fetched only when the preview opens (or on
 * mount when `prefetch` is true). File metadata is cached for the lifetime of
 * the pre-signed S3 URL, or 10 minutes by default. File contents for a given
 * pre-signed URL are cached until the component unmounts, after which they are
 * garbage collected within 5 minutes if the component is not rendered again.
 */
export const FileDataDisplay = ({
  fileName,
  fileId,
  slotLeft,
  prefetch = false,
  hideIcon = false,
  alignment,
  shouldTruncateFilename,
  shouldInheritTypography = false,
  isInline,
  isBusy,
  isLoading,
  emptyText = "-",
  showInvalidIndicator = true,
  invalidText = "Unsupported data type",
  unsafeText = "File was deemed unsafe",
  scanPollConfig,
  className,
  style,
}: FileDataDisplayProps) => {
  const [shouldFetchDetails, setShouldFetchDetails] = useState(prefetch);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string>("");
  const { toast } = useToast();
  const {
    data: rawFileMeta,
    error: fileMetaError,
    isInitialLoading: isFileMetaInitialLoading,
  } = useFileDownloadMeta(fileId, {
    enabled: shouldFetchDetails,
    pollSleepTimeMs: scanPollConfig?.pollSleepTimeMs,
  });

  const isScanComplete = rawFileMeta ? isFileScanComplete(rawFileMeta) : false;
  // a still-pending scan (or a safe file not yet in the archived bucket) has no
  // usable download link yet, so it should read as "loading" rather than ready
  const isScanPending = Boolean(rawFileMeta) && !isScanComplete;

  // only expose terminal metadata: unsafe (no uri) or safe with an archived uri,
  // mirroring the normalization previously done inside the query function
  const fileMeta = useMemo<FileDownloadMetaType | undefined>(() => {
    if (!rawFileMeta || !isScanComplete) {
      return undefined;
    }

    if (rawFileMeta.file_scan_status === FileScanStatus.UNSAFE) {
      return {
        file_id: rawFileMeta.file_id,
        file_scan_status: FileScanStatus.UNSAFE,
      };
    }

    return {
      file_id: rawFileMeta.file_id,
      file_scan_status: FileScanStatus.SAFE,
      uri: rawFileMeta.uri,
    };
  }, [rawFileMeta, isScanComplete]);
  const {
    data: fileBlob,
    error: fileBlobError,
    isInitialLoading: isDataInitialLoading,
  } = useQuery({
    queryKey: ["files", fileMeta?.file_id, fileMeta?.uri],
    queryFn: () => {
      if (!fileMeta?.uri) {
        return;
      }

      return fetch(fileMeta.uri).then((r) => r.blob());
    },
    enabled: Boolean(fileMeta?.uri && shouldFetchDetails),
    staleTime: Infinity,
    meta: {
      hideErrorToasts: true,
    },
  });
  const downloadFileMutation = useMutation({
    mutationFn: async ({
      fileName,
      fileBlob,
    }: {
      fileName: string;
      fileBlob?: Blob;
    }) => {
      downloadFile(fileName, fileBlob);
    },
    onError: (err) => {
      toast.push({
        intent: "error",
        description: `${fileName} failed to download.`,
      });
      console.error(err);
    },
  });

  useEffect(() => {
    if (!fileBlob) {
      return;
    }

    const uri = URL.createObjectURL(fileBlob);

    setFilePreviewUrl(uri);

    return () => URL.revokeObjectURL(uri);
  }, [fileBlob]);

  const handlePreviewOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setShouldFetchDetails(true);
    }
  };

  const handleDownload = () => {
    if (!fileName || !fileId) return;

    if (fileMeta?.file_scan_status === FileScanStatus.UNSAFE) {
      toast.push({
        intent: "error",
        description: `Can't download ${fileName}. File is unsafe.`,
      });
      return;
    }

    // Prefer a cached uri from a prior successful fetch. A later metadata
    // refresh can fail (and set fileMetaError) without invalidating that uri.
    if (!fileMeta?.uri) {
      toast.push({
        intent: "error",
        description: `Can't download ${fileName}. Unable to fetch the file download link.`,
      });
      ExceptionUtils.reportException(fileMetaError, "warning", {
        hint: `File could not be downloaded from a file download link. (name: ${fileName}, ref: ${fileId})`,
      });
      return;
    }

    if (shouldFetchDetails && error) {
      toast.push({
        intent: "error",
        description: `Can't download ${fileName}. Unable to fetch the file.`,
      });
      ExceptionUtils.reportException(fileBlobError, "warning", {
        hint: `File could not be downloaded from a file download link. (name: ${fileName}, ref: ${fileId})`,
      });
      return;
    }

    if (shouldFetchDetails) {
      downloadFileMutation.mutate({ fileName, fileBlob });
    } else {
      fetch(fileMeta.uri)
        .then((r) => r.blob())
        .then((blob) =>
          downloadFileMutation.mutate({
            fileName,
            fileBlob: blob,
          })
        )
        .catch((err) => {
          toast.push({
            intent: "error",
            description: `Can't download ${fileName}. Unable to fetch the file.`,
          });
          ExceptionUtils.reportException(err, "warning", {
            hint: `File could not be downloaded from a file download link. (name: ${fileName}, ref: ${fileId})`,
          });
        });
    }
  };

  const hasUsableFileMeta =
    Boolean(fileMeta?.uri) ||
    fileMeta?.file_scan_status === FileScanStatus.UNSAFE;
  const error = (fileMetaError && !hasUsableFileMeta) || fileBlobError;
  const isUnsafe = fileMeta?.file_scan_status === FileScanStatus.UNSAFE;
  const isInvalid = typeof fileName !== "string" && validate(fileId ?? "");
  const isEmpty = !fileName || !fileId;
  const showWarningAdornment = showInvalidIndicator && !isEmpty && isInvalid;
  const isAnyLoading =
    isLoading ||
    isFileMetaInitialLoading ||
    isScanPending ||
    isDataInitialLoading;
  const fileExtension = fileName
    ? getFileExtensionFromFilename(fileName)
    : null;
  const fileTypeRenderable = getBrowserRenderableFileType(fileExtension);
  const mimeType =
    getMimeTypeFromFileName(fileName ?? "") ??
    getMimeTypeFromExtension(fileExtension);

  return (
    <DataDisplayWithAdornment
      showAdornment={showWarningAdornment}
      alignment={alignment}
      isInline={isInline}
      isBusy={isBusy}
      isLoading={isLoading}
      adornmentSlot={() =>
        showWarningAdornment ? (
          <Icon
            variant="warning"
            colour={colours.colours.status.warning}
            size={12}
            alt={invalidText}
            aria-roledescription="Warning Icon"
          />
        ) : null
      }
      className={className}
      style={style}
    >
      {fileName && fileId ? (
        isUnsafe ? (
          <span
            className={cx("unsafe-file")}
            title={fileName}
          >
            <Icon
              variant="warning"
              colour={colours.colours.status.warning}
              size={16}
              className={cx("unsafe-file-icon")}
              alt={unsafeText}
              aria-roledescription="Warning Icon"
            />
            {unsafeText}
          </span>
        ) : (
          <FilePreviewPopover
            fileName={fileName}
            fileType={fileTypeRenderable}
            fileMimeType={mimeType}
            fileExtension={fileExtension}
            fileObjectUrl={filePreviewUrl}
            onDownload={handleDownload}
            isLoading={isAnyLoading}
            rootProps={{
              onOpenChange: handlePreviewOpenChange,
            }}
          >
            <Button
              slotLeft={
                hideIcon
                  ? null
                  : (slotLeft ?? (
                      <Icon
                        variant="paperclip"
                        size={12}
                        className={cx("preview-trigger__icon")}
                      />
                    ))
              }
              variant={error || isInvalid ? "text-error" : "text"}
              size="xx-small"
              className={cx(
                "preview-trigger",
                shouldInheritTypography &&
                  "preview-trigger--inherit-typography",
                shouldTruncateFilename && "truncate"
              )}
            >
              {fileName ?? emptyText}
            </Button>
          </FilePreviewPopover>
        )
      ) : (
        emptyText
      )}
    </DataDisplayWithAdornment>
  );
};
